<template>
  <div>

    <!-- DESKTOP VIEW -->
    <b-card id="cleanings-planning-card" class="d-none d-md-block">
      <b-row class="d-flex justify-content-between align-items-center mb-4">
        <b-col cols="12" sm="auto" class="mb-1 mb-sm-0">
          <h4 class="mb-0">{{ weekText }}</h4>
        </b-col>
        <b-col cols="12" sm="auto" class="d-flex">
          <v-select v-model="selectedDaysNumber" :options="selectedDaysOptions" :reduce="(option) => option.value"
            class="days-selector flex-shrink-0 mr-2" />
          <b-button-group size="sm">
            <b-button v-b-tooltip.hover.bottom="'Període anterior'" variant="secondary" @click="previousWeek">
              <feather-icon icon="ChevronLeftIcon" />
            </b-button>
            <b-button v-b-tooltip.hover.bottom="'Avui'" variant="secondary" @click="setToday">
              <feather-icon icon="CircleIcon" />
            </b-button>
            <b-button v-b-tooltip.hover.bottom="'Període següent'" variant="secondary" @click="nextWeek">
              <feather-icon icon="ChevronRightIcon" />
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <cleanings-planning-table :bookings="parsedCleaningsPlanning" :loading="effectiveLoading"
        @toggle-cleaning="onToggleCleaning" />
        </b-col>
      </b-row>
    </b-card>

    <!-- MOBILE VIEW -->
    <cleanings-planning-list class="d-block d-md-none" :bookings="parsedCleaningsPlanning" :loading="effectiveLoading"
      :week-text="weekText" :selected-days-number.sync="selectedDaysNumber" :selected-days-options="selectedDaysOptions"
      @previous-period="previousWeek" @set-today="setToday" @next-period="nextWeek"
      @toggle-cleaning="onToggleCleaning" />

    <!-- CONFIRM MODAL -->
    <b-modal
      v-model="confirmModalVisible"
      :title="pendingToggle && pendingToggle.cleaningCompleted ? 'Marcar neteja com a pendent' : 'Marcar neteja com a completada'"
      centered
      hide-footer
      @hidden="pendingToggle = null"
    >
      <div v-if="pendingToggle" class="px-25 pb-25">
        <p class="mb-2">
          <span v-if="pendingToggle.cleaningCompleted">
            Vols marcar la neteja de <strong>{{ pendingToggle.accommodation }}</strong>
            ({{ pendingToggle.localizator }}) com a <strong>pendent</strong>?
          </span>
          <span v-else>
            Vols marcar la neteja de <strong>{{ pendingToggle.accommodation }}</strong>
            ({{ pendingToggle.localizator }}) com a <strong>completada</strong>?
          </span>
        </p>
        <b-button
          block
          variant="primary"
          :disabled="updatingCleaning"
          class="mb-75"
          @click="confirmToggleCleaning"
        >
          <b-spinner v-if="updatingCleaning" small class="mr-50" />
          {{ pendingToggle.cleaningCompleted ? 'Marcar com a pendent' : 'Marcar com a completada' }}
        </b-button>
        <b-button
          block
          variant="outline-secondary"
          :disabled="updatingCleaning"
          @click="confirmModalVisible = false"
        >
          Cancel·lar
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButtonGroup, BButton, BModal, BSpinner, VBTooltip,
} from "bootstrap-vue";
import { formatDateObjectToDate } from "@formatters";
import { notifyError } from "@methods";
import CleaningsPlanningTable from "@/views/cleaning/components/CleaningsPlanningTable.vue";
import CleaningsPlanningList from "@/views/cleaning/components/CleaningsPlanningList.vue";
import _debounce from "lodash/debounce";
import vSelect from "vue-select";

export default {
  components: {
    BCard, BRow, BCol, CleaningsPlanningTable, CleaningsPlanningList, BButtonGroup, BButton, BModal, BSpinner, vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      weekStart: this.$moment().startOf("day"),
      isNavigating: false,
      selectedDaysNumber: 14,
      confirmModalVisible: false,
      pendingToggle: null,
      updatingCleaning: false,
      selectedDaysOptions: [
        { label: "1 dia", value: 1 },
        { label: "3 dies", value: 3 },
        { label: "7 dies", value: 7 },
        { label: "14 dies", value: 14 },
        { label: "30 dies", value: 30 },
        { label: "90 dies", value: 90 },
      ],
    };
  },
  computed: {
    loadingPlanning() {
      return this.$store.getters["bookings/loadingPlanning"];
    },
    effectiveLoading() {
      return this.isNavigating || this.loadingPlanning;
    },
    cleaningsPlanning() {
      if (this.effectiveLoading) return [];

      const checkinsPlanning = this.$store.getters["bookings/checkinsPlanning"];

      return checkinsPlanning.filter((booking) => {
        if (!booking.contract?.cleaningProvided) return false;

        const startDate = this.$moment(this.weekStart).startOf("day");
        const endDate = this.$moment(this.weekStart).add(this.selectedDaysNumber, "days").startOf("day");
        const checkin = this.$moment(booking.checkin).startOf("day");

        return checkin.isSameOrAfter(startDate) && checkin.isBefore(endDate);
      });
    },
    parsedCleaningsPlanning() {
      if (!this.cleaningsPlanning.length) return null;

      return this.cleaningsPlanning.map((booking) => ({
        uuid: booking.uuid || this.$t("No definit"),
        date: new Date(booking.date) || this.$t("No definit"),
        localizator: booking.localizator || this.$t("No definit"),
        source: booking.source || null,
        guests: this.bookingGuests(booking),
        accommodation: booking.accommodation?.name || this.$t("No definit"),
        accommodationUuid: booking.accommodation?.uuid || null,
        accommodationSlug: booking.accommodation?.slug || null,
        client: booking.client?.fullName || this.$t("No definit"),
        checkin: new Date(booking.checkin) || this.$t("No definit"),
        checkinTime: booking.details?.accommodationArrivalTime || this.$t("No definit"),
        checkout: new Date(booking.checkout) || this.$t("No definit"),
        checkoutTime: booking.details?.accommodationDepartureTime || this.$t("No definit"),
        babyChairs: booking.details?.babyChairs || null,
        babyChairsText: this.bookingBabyChairs(booking),
        babyCots: booking.details?.babyCots || null,
        babyCotsText: this.bookingBabyCots(booking),
        highlighted: booking.highlighted || false,
        cleaningProviders: booking.contract?.cleaningProviders || [],
        bedLinenChange: (booking.bedLinenChanges || []).map((d) => new Date(d)),
        cleaningCompleted: booking.stats?.cleaningCompleted ?? false,
        statsUuid: booking.stats?.uuid || null,
      }));
    },
    weekStartDate() {
      return this.weekStart.toDate();
    },
    weekStartText() {
      return formatDateObjectToDate(this.weekStartDate, this.$i18n.locale, {
        month: "2-digit", day: "2-digit", year: "numeric",
      });
    },
    weekEndDate() {
      return this.$moment(this.weekStartDate).add(this.selectedDaysNumber - 1, "days").toDate();
    },
    weekEndText() {
      return formatDateObjectToDate(this.weekEndDate, this.$i18n.locale, {
        month: "2-digit", day: "2-digit", year: "numeric",
      });
    },
    weekText() {
      if (this.selectedDaysNumber === 1) return `Dia ${this.weekStartText}`;
      return `Del ${this.weekStartText} al ${this.weekEndText}`;
    },
  },
  created() {
    this.fetchPlanning(this);
  },
  watch: {
    loadingPlanning(val) {
      if (!val) this.isNavigating = false;
    },
    weekStart() {
      this.fetchPlanning(this);
    },
    selectedDaysNumber() {
      this.isNavigating = true;
      this.fetchPlanning(this);
    },
  },
  methods: {
    fetchPlanning: _debounce((self) => {
      self.$store
        .dispatch("bookings/fetchPlanning")
        .catch(() => {
          notifyError(
            self.$t("errors.fetchBooking.title"),
            self.$t("errors.fetchBooking.description")
          );
        });
    }, 1000),
    bookingBabyChairs(booking) {
      if (!booking?.details || booking.details.babyChairs == null) return `? ${this.$t("trones")}`;
      const n = booking.details.babyChairs;
      return n === 0 ? this.$t("No") : `${n} ${this.$t("trones")}`;
    },
    bookingBabyCots(booking) {
      if (!booking?.details || booking.details.babyCots == null) return `? ${this.$t("cunes")}`;
      const n = booking.details.babyCots;
      return n === 0 ? this.$t("No") : `${n} ${this.$t("cunes")}`;
    },
    bookingGuests(booking) {
      return `${booking.adults || 0}-${booking.children || 0}-${booking.babies || 0}`;
    },
    nextWeek() {
      this.isNavigating = true;
      this.weekStart = this.$moment(this.weekStart).add(this.selectedDaysNumber, "days");
    },
    previousWeek() {
      this.isNavigating = true;
      this.weekStart = this.$moment(this.weekStart).subtract(this.selectedDaysNumber, "days");
    },
    setToday() {
      this.isNavigating = true;
      this.weekStart = this.$moment().startOf("day");
    },
    onToggleCleaning(booking) {
      this.pendingToggle = booking;
      this.confirmModalVisible = true;
    },
    confirmToggleCleaning() {
      if (!this.pendingToggle) return;
      this.updatingCleaning = true;
      const { statsUuid, cleaningCompleted } = this.pendingToggle;
      this.$store
        .dispatch("bookings/updateBookingStats", { uuid: statsUuid, cleaningCompleted: !cleaningCompleted })
        .then(() => {
          this.confirmModalVisible = false;
        })
        .catch(() => {
          notifyError(
            this.$t("errors.fetchBooking.title"),
            this.$t("errors.fetchBooking.description")
          );
        })
        .finally(() => {
          this.updatingCleaning = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#cleanings-planning-card {
  .days-selector {
    min-width: 150px;
  }
}
</style>
